import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { Button, H2 } from "@/components/bluebird/primitives";
import { BreadcrumbJsonLd, FAQPageJsonLd } from "@/components/shared/json-ld";
import { color, font, maxWidth, radius, space } from "@/lib/design-tokens";
import { faq } from "@/lib/copy/faq";
import { shop } from "@/lib/copy/shop";

/**
 * `/faq` lost its nav slot and keeps its footer link — it is the one route in
 * the handoff's keep-alive list with no home among the comp's own footer
 * labels, which is why the footer carries a fifth link there and why
 * `script/design_diff.mjs` reports that as a named deviation.
 *
 * The accordion is a native `<details>` element rather than a JS component:
 * it opens without JavaScript, it is searchable by the browser's own find, and
 * it needs no ARIA wiring to be correct.
 */
function FAQ() {
  const allQuestions = faq.categories.flatMap((c) =>
    c.questions.map((q) => ({ question: q.q, answer: q.a }))
  );

  return (
    <>
      <BreadcrumbJsonLd items={[{ name: "FAQ", href: "/faq" }]} />
      <FAQPageJsonLd faqs={allQuestions} />

      <SubMasthead
        current="/faq"
        photo="hero-faq"
        photoAlt="An electric cargo bike on a park path"
      >
        <SubHero
          line1={faq.hero.line1}
          line2={faq.hero.line2}
          intro={faq.hero.intro}
          introMaxWidth="620px"
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>

      {faq.categories.map((category) => (
        <section
          key={category.id}
          id={category.id}
          data-section={category.id}
          style={{
            maxWidth,
            margin: "0 auto",
            padding: `clamp(40px,6vw,72px) ${space.gutter} 0`,
          }}
        >
          <div
            style={{
              fontFamily: font.mono,
              fontSize: "12px",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: color.slateLight,
            }}
          >
            {category.name}
          </div>
          <div style={{ marginTop: "18px", borderTop: `1px solid ${color.border}` }}>
            {category.questions.map((item) => (
              <details
                key={item.q}
                style={{ borderBottom: `1px solid ${color.border}`, padding: "18px 0" }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "18px",
                    lineHeight: 1.4,
                    listStyle: "none",
                  }}
                >
                  {item.q}
                </summary>
                <p
                  style={{
                    margin: "12px 0 0",
                    fontSize: "16.5px",
                    lineHeight: 1.6,
                    color: color.slate,
                    maxWidth: "68ch",
                  }}
                >
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      ))}

      <section
        data-section="stuck"
        style={{ maxWidth, margin: "0 auto", padding: `${space.sectionY} ${space.gutter}` }}
      >
        <div
          style={{
            background: color.gold,
            borderRadius: radius.panel,
            padding: "clamp(28px,4vw,48px)",
          }}
        >
          <H2 style={{ color: color.ink }}>{faq.stillStuckHeading}</H2>
          <p style={{ margin: "14px 0 0", fontSize: "16.5px", lineHeight: 1.55, color: color.onGold, maxWidth: "52ch" }}>
            {faq.stillStuckBody}
          </p>
          <div style={{ marginTop: "24px", display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <Button href={shop.phoneHref} variant="blue" style={{ fontSize: "16px", padding: "15px 26px" }}>
              Call {shop.phone}
            </Button>
            <Button
              href="/contact"
              variant="outlineBlue"
              style={{ border: `2px solid ${color.blue}`, fontSize: "16px", padding: "13px 26px" }}
            >
              Write instead
            </Button>
          </div>
        </div>
      </section>

      </main>

      <SlimFooter borderTop />
    </>
  );
}

FAQ.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default FAQ;
